<template>
  <div class="order-page">
    <div class="form-container">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜索订单"
        class="search-input"
        @input="handleSearch"
      />
      <input
        type="date"
        v-model="orderTime"
        class="date-input"
        @change="handleSearch"
      />
      <select
        v-model="selectedOptions"
        class="select-input"
        @change="handleSearch"
      >
        <option value="">请选择客户</option>
        <option
          v-for="user in users"
          :key="user.name"
          :value="user.id"
        >{{ user.name }}</option>
      </select>
      <select
        v-model="selectedStatus"
        class="select-input"
        @change="handleSearch"
      >
        <option value="">请选择订单状态</option>
        <option
          v-for="status in statusOptions"
          :key="status.value"
          :value="status.value"
        >{{ status.label }}</option>
      </select>
      <button
        class="btn btn-primary"
        @click="addDialogVisible = true"
      >
        <span class="icon">+</span> 添加订单
      </button>
    </div>

    <table class="data-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>用户名</th>
          <th>订单号</th>
          <th>订单内容</th>
          <th>下单时间</th>
          <th>平仓价</th>
          <th>客户</th>
          <th>期权公司</th>
          <th>到期日期</th>
          <th>状态</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in filteredOrders" :key="row.id" class="table-row">
          <td>{{ row.id }}</td>
          <td>{{ row.user?.username || '—' }}</td>
          <td>{{ row.orderNumber }}</td>
          <td class="order-content">
            <div v-for="(line, lineIndex) in formattedLines(row.orderContent)" :key="lineIndex">
              <a
                v-if="isLinkableLine(line, lineIndex, row.options)"
                :href="generateLink(line)"
                target="_blank"
                class="link-style"
              >
                {{ line }}
              </a>
              <span v-else>{{ line }}</span>
            </div>
          </td>
          <td>{{ row.orderTime }}</td>
          <td>{{ row.sellPrice === 0 ? '—' : row.sellPrice }}</td>
          <td>{{ row.customer?.name || '—' }}</td>
          <td>{{ row.options || '—' }}</td>
          <td>{{ row.endTime || '—' }}</td>
          <td>
            <label class="switch">
              <input 
                type="checkbox" 
                :checked="row.status === 1"
                @change="handleStatusChange(row)"
              >
              <span class="slider round" :class="{ 'active-success': row.status === 1 }"></span>
            </label>
          </td>
          <td class="action-buttons">
            <button
              class="btn btn-small btn-primary"
              @click="convertToTable(index, row)"
              title="回执单"
            >
              <span class="icon">📄</span>
            </button>
            <button
              class="btn btn-small btn-success"
              @click="showCalculation(index, row)"
              title="收益"
            >
              <span class="icon">🧮</span>
            </button>
            <button
              class="btn btn-small btn-warning"
              @click="sellOrder(index, row)"
              title="平仓指令"
            >
              <span class="icon">📝</span>
            </button>
            <button
              class="btn btn-small btn-info"
              @click="editOrder(index, row)"
              title="编辑"
            >
              <span class="icon">✏️</span>
            </button>
            <button
              class="btn btn-small btn-danger"
              @click="deleteOrder(index, row)"
              title="删除"
            >
              <span class="icon">🗑️</span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pagination-container">
      <div class="pagination-info">共 {{ total }} 条记录</div>
      <div class="pagination-controls">
        <button 
          class="btn btn-small" 
          :disabled="currentPage === 1"
          @click="handleCurrentChange(currentPage - 1)"
        >
          上一页
        </button>
        <span v-for="page in visiblePages" :key="page" class="page-item">
          <button 
            class="btn btn-small"
            :class="{ 'btn-primary': page === currentPage }"
            @click="handleCurrentChange(page)"
          >
            {{ page }}
          </button>
        </span>
        <button 
          class="btn btn-small" 
          :disabled="currentPage === totalPages"
          @click="handleCurrentChange(currentPage + 1)"
        >
          下一页
        </button>
        <select v-model="pageSize" class="page-size-select" @change="handleSizeChange">
          <option v-for="size in pageSizes" :key="size" :value="size">
            {{ size }}条/页
          </option>
        </select>
      </div>
    </div>

    <!-- 添加订单弹窗 -->
    <div v-if="addDialogVisible" class="dialog-overlay">
      <div class="dialog">
        <div class="dialog-header">
          <h3>添加订单</h3>
          <button class="dialog-close" @click="addDialogVisible = false">&times;</button>
        </div>
        <div class="dialog-content">
          <div class="form-group">
            <label>订单内容</label>
            <textarea v-model="newOrder.orderContent" rows="15" class="form-input"></textarea>
          </div>
          <div class="form-group">
            <label>客户</label>
            <select v-model="newOrder.customerId" class="form-input">
              <option value="">请选择客户</option>
              <option v-for="user in users" :key="user.name" :value="user.id">{{ user.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>机构</label>
            <select v-model="newOrder.options" class="form-input">
              <option value="">请选择机构</option>
              <option v-for="brokerage in brokerage_options" :key="brokerage.key" :value="brokerage.key">
                {{ brokerage.value }}
              </option>
            </select>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn" @click="addDialogVisible = false">取消</button>
          <button class="btn btn-primary" @click="handleAdd">确定</button>
        </div>
      </div>
    </div>

    <!-- 编辑订单弹窗 -->
    <div v-if="editDialogVisible" class="dialog-overlay">
      <div class="dialog">
        <div class="dialog-header">
          <h3>编辑订单</h3>
          <button class="dialog-close" @click="editDialogVisible = false">&times;</button>
        </div>
        <div class="dialog-content">
          <div class="form-group">
            <label>订单编号</label>
            <input type="text" v-model="newOrder.orderNumber" class="form-input">
          </div>
          <div class="form-group">
            <label>订单内容</label>
            <textarea v-model="newOrder.orderContent" rows="15" class="form-input"></textarea>
          </div>
          <div class="form-group">
            <label>平仓价</label>
            <input type="number" v-model="newOrder.sellPrice" class="form-input" step="0.0001">
          </div>
          <div class="form-group">
            <label>客户</label>
            <select v-model="newOrder.customerId" class="form-input">
              <option value="">请选择客户</option>
              <option v-for="user in users" :key="user.name" :value="user.id">{{ user.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>机构</label>
            <select v-model="newOrder.options" class="form-input">
              <option value="">请选择机构</option>
              <option v-for="brokerage in brokerage_options" :key="brokerage.value" :value="brokerage.value">
                {{ brokerage.value }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>到期时间</label>
            <input type="date" v-model="newOrder.endTime" class="form-input">
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="newOrder.status" :true-value="1" :false-value="0"> 状态
            </label>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn" @click="editDialogVisible = false">取消</button>
          <button class="btn btn-primary" @click="handleUpdate">确定</button>
        </div>
      </div>
    </div>

    <!-- 回执单弹窗 -->
    <div v-if="showReceiptDialog" class="dialog-overlay">
      <div class="dialog">
        <div class="dialog-header">
          <h3>回执单</h3>
          <button class="dialog-close" @click="showReceiptDialog = false">&times;</button>
        </div>
        <div class="dialog-content">
          <div class="table-container" v-if="tableData.length">
            <table class="styled-table">
              <tbody>
                <tr
                  v-for="(row, index) in tableData"
                  :key="index"
                  :class="{ 'bg-blue': index % 2 === 0, 'bg-green': index % 2 === 1 }"
                >
                  <td v-for="cell in row" :key="cell">{{ cell }}</td>
                </tr>
                <tr :class="{ 'bg-blue': tableData.length % 2 === 0, 'bg-green': tableData.length % 2 === 1 }">
                  <td>行权价格</td>
                  <td>{{ price }}</td>
                </tr>
                <tr :class="{ 'bg-blue': (tableData.length + 1) % 2 === 0, 'bg-green': (tableData.length + 1) % 2 === 1 }">
                  <td>合计费用</td>
                  <td>{{ totalCost }}</td>
                </tr>
                <tr :class="{ 'bg-blue': (tableData.length + 2) % 2 === 0, 'bg-green': (tableData.length + 2) % 2 === 1 }">
                  <td>敲出规则</td>
                  <td>连续三个涨跌停板则敲出</td>
                </tr>
                <tr :class="{ 'bg-blue': (tableData.length + 3) % 2 === 0, 'bg-green': (tableData.length + 3) % 2 === 1 }">
                  <td>最快行权日</td>
                  <td>T+1</td>
                </tr>
              </tbody>
            </table>
            <button @click="copyTableToImage" class="btn btn-primary copy-button">
              <span class="icon">📋</span> 复制回执单
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 收益计算弹窗 -->
    <div v-if="showCalculationDialog" class="dialog-overlay">
      <div class="dialog">
        <div class="dialog-header">
          <h3>收益计算</h3>
          <button class="dialog-close" @click="showCalculationDialog = false">&times;</button>
        </div>
        <div class="dialog-content">
          <CalculationTab :stockHandler="stockData" :calculationCallback="calculate" />
        </div>
      </div>
    </div>

    <!-- 消息提示组件 -->
    <div v-if="message.show" class="message-toast" :class="message.type">
      {{ message.text }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CalculationTab from '@/components/CalculationTab.vue';
import { formatDate } from '@/utils/commonMethods';
import { copyTableToImage } from '@/utils/utils';

export default {
  components: {
    CalculationTab,
  },
  data() {
    return {
      orderTime: '',
      searchQuery: '',
      orders: [],
      filteredOrders: [],
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50],
      total: 0,
      newOrder: {
        uid: '',
        orderNumber: '',
        orderContent: '',
        orderTime: '',
        sellPrice: 0.0,
        status: 0,
        options :'',
        endTime: '',
        customerId: "",
        "customer": {
              "id": 0,
                "name": "",
                "email": "",
                "phone": "",
                "address": "",
                "created_at": ""
            }
      },
      currentEditIndex: -1,
      addDialogVisible: false,
      editDialogVisible: false,
      showReceiptDialog: false,
      selectedStatus:0,
      statusOptions: [ // 状态选项
          { value: 0, label: '进行中' },
          { value: 1, label: '已平仓' }
       ],
      brokerage_options: [
        { value: '银河', key: '银河' },
        { value: '华泰', key: '华泰' },
        { value: '中金', key: '中金' },
        { value: '中信', key: '中信' },
        { value: '永安', key: '永安' },
        { value: '浙期', key: '浙期' },
        { value: '国君', key: '国君' },
        { value: '国君', key: '广发' }

      ],
      showCalculationDialog: false,
      tableData: [],
      stockData: [],
      totalCost: 0,
      price: 0,
      users: [], // 添加用户列表
      message: {
        show: false,
        text: '',
        type: 'info' // success, error, warning, info
      }
    };
  },
  created() {
    this.fetchCustomers();
    this.handleSearch(); // 👈 初始化时触发搜索
  },
   computed: {
    formattedDate() {
      return this.orderTime ? formatDate(this.orderTime) : '';
    },
    // 计算总页数
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },
    // 计算可见页码
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const visible = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          visible.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            visible.push(i);
          }
          visible.push('...', total);
        } else if (current >= total - 3) {
          visible.push(1, '...');
          for (let i = total - 4; i <= total; i++) {
            visible.push(i);
          }
        } else {
          visible.push(1, '...');
          for (let i = current - 1; i <= current + 1; i++) {
            visible.push(i);
          }
          visible.push('...', total);
        }
      }
      
      return visible;
    },
  },
  methods: {
    // 自定义消息提示
    showMessage(text, type = 'info') {
      this.message = {
        show: true,
        text,
        type
      };
      // 3秒后自动关闭
      setTimeout(() => {
        this.message = { show: false, text: '', type: 'info' };
      }, 3000);
    },
      orderLines() {
        return (content) => {
          return content
            .replace(/\\n/g, '\n') // 解析转义换行符
            .split('\n')
            .map(line => line.trim()) // 去除首尾空格
        }
      },

    formattedLines(content) { // 修正方法名
        return content
          .replace(/\\n/g, '\n') // 解析转义换行符
          .split('\n')
          .map(line => line.trim()) // 去除首尾空格
      },
       // 新增配置映射
        getValidIndices(options) {
          return {
            '华泰': [0, 1],
            '银河': [2, 3],
            // 可添加其他机构配置
          }[options] || []
        },
       // 修改后的判断方法
        isLinkableLine(line, index, options) {
          const validIndices = this.getValidIndices(options);
          return (
            validIndices.includes(index) &&
            line.includes(' ') &&
            line.split(' ')[1]
          );
        },

      // 生成带编码的URL
      generateLink(line) {
        const code = line.split(' ')[1]
        return `https://www.iwencai.com/unifiedwap/result?querytype=stock&w=${encodeURIComponent(code)}`
      },
     copyTableToImage() {
      const table = document.querySelector('.styled-table');
      const firstRow = table.rows[1];
      const thirdCell = firstRow.cells[2];

      const fileName = '个股【'+thirdCell+'】期权查询情况_' + this.currentDate + '.png';
      copyTableToImage(table, fileName);
    },
    handleStatusChange(row) {

      const status = row.status
      console.log('Status changed to:', status);
      axios
        .put(`/api/orders/${row.id}`, { status: status }, {
          headers: { 'Authorization': `Bearer ${this.$store.getters.token}` },
        })
        .then(() => {
          this.showMessage('状态更新成功', 'success');
        })
        .catch((error) => {
          this.showMessage('状态更新失败', 'error');
          row.status = !row.status; // 回滚状态
          console.error('状态更新失败:', error);
        });
    },
    convertToTable(index, row) {
      const clonedRow = JSON.parse(JSON.stringify(row));
      clonedRow.orderContent = clonedRow.orderContent.replace(/\\n/g, '\n');
      if (clonedRow.options=='银河'){
          this.tableData = clonedRow.orderContent
            .split('\n')
            .filter((line, idx) => ![0, 1, 6, 11, 12].includes(idx))
            .map((line) => line.split(':').map((cell) => cell.trim()));
            if (this.tableData.length >= 5) {
                const optionRate = parseFloat(this.tableData[4][1]);
                const price = parseFloat(this.tableData[5][1].split('%')[0]);
                this.totalCost = Math.round(optionRate / 100 * price * 10000);
              }
              if (this.tableData.length >= 8) {
                const parsedPrice = parseFloat(this.tableData[8][1]);
                if (!isNaN(parsedPrice)) this.price = parsedPrice;
              }
      }else if(clonedRow.options=='华泰'){
          this.tableData = clonedRow.orderContent
            .split('\n')
            .filter((line, idx) => ![2,3,4].includes(idx))
            .map((line) => line.split(':').map((cell) => cell.trim()));
           if (this.tableData.length >= 5) {
                const optionRate = parseFloat(this.tableData[10][1]);
                const price = parseFloat(this.tableData[10][1].split('%')[0]);
                this.totalCost = Math.round(optionRate / 100 * price * 10000);
              }
              if (this.tableData.length >= 8) {
                const parsedPrice = parseFloat(this.tableData[10][1]);
                if (!isNaN(parsedPrice)) this.price = parsedPrice;
              }
      }

      this.showReceiptDialog = true;
    },
   sellOrder(index, row) {
      const clonedRow = JSON.parse(JSON.stringify(row));
      clonedRow.orderContent = clonedRow.orderContent.replace(/\\n/g, '\n');
      const data = clonedRow.orderContent.split('\n');

      // 验证数据有效性（可选）
      if (!data[1] || !data[2] || !data[3] || !data[8] || !data[13]) {
        this.showMessage('订单数据不完整，无法复制', 'error');
        return;
      }

      // 生成文本内容
      let text =
        "单号：" + data[1].split(': ')[1] + "\n" +
        "标的：" + data[2].split(': ')[1] + " " + data[3].split(': ')[1] + "\n" +
        "期初：" + data[13].split(': ')[1] + "\n" +
        "名本：" + data[8].split(': ')[1] + "\n" +
        "平仓指令：市价/限价/" + clonedRow.sellPrice + "元\n";

      // 复制到剪贴板
      navigator.clipboard.writeText(text)
        .then(() => {
          this.showMessage('内容已复制到剪贴板', 'success');
        })
        .catch((error) => {
          console.error('复制失败:', error);
          this.showMessage('复制失败，请检查权限', 'error');
        });
    },

    showCalculation(index, row) {
      const clonedRow = JSON.parse(JSON.stringify(row));
      clonedRow.orderContent = clonedRow.orderContent.replace(/\\n/g, '\n');
      const data = clonedRow.orderContent.split('\n');
      this.stockData = {
        code: `${data[3].split(': ')[1]} ${data[2].split(': ')[1]}`,
        buyPrice: data[13].split(': ')[1],
        sellPrice: clonedRow.sellPrice,
        optionRate: data[7].split(': ')[1].replace('%', ''),
        knockOutPrice: data[13],
      };
      this.showCalculationDialog = true;
    },
    calculate(
      formattedBuyPrice,
      formattedSellPrice,
      formattedOptionRate,
      formattedKnockOutPrice
    ) {
      const BuyPrice = formattedBuyPrice;
      const SellPrice = formattedSellPrice;
      const OptionRate = formattedOptionRate;
      const KnockOutPrice = formattedKnockOutPrice;
      const total = 1000000;
      const fee = 0.01;
      const fee_total = 0.2;
      const fee_amount = ((fee * OptionRate * total) + fee_total).toFixed(2);
      const amount = (SellPrice - BuyPrice) / BuyPrice;

      const earnings = (amount * total).toFixed(2);
      const profit = (earnings - fee_amount).toFixed(2);
      const funds_fee = ((profit / fee_amount) * 100).toFixed(2);

      const knock_out_earnings = (
        (KnockOutPrice - BuyPrice) / BuyPrice * total
      ).toFixed(2);
      const knock_out_profit = (
        knock_out_earnings - fee_amount
      ).toFixed(2);
      const knock_out_funds_fee = (
        (knock_out_profit / fee_amount) * 100
      ).toFixed(2);

      const js = (1 + (parseFloat(OptionRate) + parseFloat(fee_total)) / 100) * BuyPrice;
      return [
        {
          "持仓成本": (fee_amount / 10000).toFixed(2) + "万元",
          "持仓股数": (total / BuyPrice).toFixed(2),
          "回本价格": js.toFixed(2) + "元",
          "标的涨幅": (amount * 100).toFixed(2) + "%",
        },
        {
          "期末收益": (earnings / 10000).toFixed(2) + "万元",
          "净收益": (profit / 10000).toFixed(2) + "万元",
          "收益率": funds_fee + "%",
        },
        {
          "敲出收益": (knock_out_earnings / 10000).toFixed(2) + "元",
          "敲出净收益": (knock_out_profit / 10000).toFixed(2) + "元",
          "敲出收益率": knock_out_funds_fee + "%",
        },
      ];
    },
   handleSearch() {
      this.currentPage = 1;
      this.fetchOrders();
    },

    fetchOrders() {
      axios
        .get('/api/orders', {
          headers: { 'Authorization': `Bearer ${this.$store.getters.token}` },
          params: {
              page: this.currentPage,
              page_size: this.pageSize,
              search:this.searchQuery,
              status:this.selectedStatus,
              customerId:this.selectedOptions,
              endTime:  this.formattedDate
          },
        })
        .then((response) => {
          this.filteredOrders=response.data.list || [];
          this.orders = response.data.list || [];
          this.total = response.data.total || 0;
        })
        .catch((error) => {
         if (error.response && error.response.data && error.response.data.detail) {
            this.showMessage(error.response.data.detail, 'error');
          } else {
             this.$message.error('获取订单失败');
          }
        });
    },
    handleSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
      this.fetchOrders();
    },
    handleCurrentChange(newPage) {
      this.currentPage = newPage;
      this.fetchOrders();
    },
    handleAdd() {
      axios
        .post('/api/orders', this.newOrder, {
          headers: { 'Authorization': `Bearer ${this.$store.getters.token}` },
        })
        .then(() => {
          this.showMessage('订单添加成功', 'success');
          this.fetchOrders();
          this.resetForm();
          this.addDialogVisible = false;
        })
        .catch((error) => {
          if (error.response && error.response.data && error.response.data.detail) {
            this.$message.error(error.response.data.detail);
          } else {
             this.showMessage('添加订单失败', 'error');
          }
        });
    },
    editOrder(index, row) {
      this.currentEditIndex = index;
      this.newOrder = { ...row };
      this.newOrder.options = row.options || '';
      this.newOrder.endTime = row.endTime || '';
      this.newOrder.orderTime = this.newOrder.orderTime || '';
      this.newOrder.status=this.newOrder.status || 0;
      this.editDialogVisible = true;
    },
    handleUpdate() {
      axios
        .put(`/api/orders/${this.newOrder.id}`, this.newOrder, {
          headers: { 'Authorization': `Bearer ${this.$store.getters.token}` },
        })
        .then(() => {
          this.showMessage('订单更新成功', 'success');
          this.fetchOrders();
          this.resetForm();
          this.editDialogVisible = false;
        })
        .catch((error) => {
          this.showMessage('订单更新失败', 'error');
          console.error('更新订单失败:', error);
        });
    },
    deleteOrder(index, row) {
      axios
        .delete(`/api/orders/${row.id}`, {
          headers: { 'Authorization': `Bearer ${this.$store.getters.token}` },
        })
        .then(() => {
          this.showMessage('订单删除成功', 'success');
          this.orders.splice(index, 1);
        })
        .catch((error) => {
          this.showMessage('订单删除失败', 'error');
          console.error('删除订单失败:', error);
        });
    },
     async fetchCustomers() {
      try {
        const res = await axios.get("/api/customers", {
          headers: { 'Authorization': `Bearer ${this.$store.getters.token}` },
          params: {
            page: this.currentPage,
            pageSize: this.pageSize
          }
        });
        this.users = res.data.data; // 当前页的数据
        this.total = res.data.total; // 总记录数
      } catch (error) {
        console.error("获取客户列表失败:", error);
        this.showMessage('获取客户列表失败', 'error');
      }
    },

    resetForm() {
      this.newOrder = {
        orderNumber: '',
        orderContent: '',
        orderTime: '',
        sellPrice: 0.0,
        status: 0,
        customerId: '',
        options :'',
      };
      this.currentEditIndex = -1;
    },
  },
};
</script>
<style scoped>
.form-container {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.table-container {
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.order-content {
  white-space: pre-line;
  word-break: break-all;
  padding: 8px;
}

.pagination-container {
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}
.link-style {
  color: #1890ff;
  text-decoration: underline;
  cursor: pointer;
}

.styled-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
}

.styled-table th {
  background-color: #409EFF;
  color: #fff !important;
  font-weight: bold;
}

.styled-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  font-size: 14px;
  color: #333;
}

/* 按钮样式 */
.btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: #fff;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn:hover {
  border-color: #40a9ff;
  color: #40a9ff;
}

.btn-primary {
  background-color: #1890ff;
  color: white;
  border-color: #1890ff;
}

.btn-primary:hover {
  background-color: #40a9ff;
  border-color: #40a9ff;
  color: white;
}

.btn-success {
  background-color: #52c41a;
  color: white;
  border-color: #52c41a;
}

.btn-success:hover {
  background-color: #73d13d;
  border-color: #73d13d;
  color: white;
}

.btn-warning {
  background-color: #faad14;
  color: white;
  border-color: #faad14;
}

.btn-warning:hover {
  background-color: #ffc53d;
  border-color: #ffc53d;
  color: white;
}

.btn-info {
  background-color: #13c2c2;
  color: white;
  border-color: #13c2c2;
}

.btn-info:hover {
  background-color: #36cfc9;
  border-color: #36cfc9;
  color: white;
}

.btn-danger {
  background-color: #ff4d4f;
  color: white;
  border-color: #ff4d4f;
}

.btn-danger:hover {
  background-color: #ff7875;
  border-color: #ff7875;
  color: white;
}

.btn-small {
  padding: 4px 8px;
  font-size: 12px;
  margin: 2px;
}

/* 输入框样式 */
.search-input,
.date-input,
.select-input,
.form-input {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  margin-right: 10px;
}

.search-input:focus,
.date-input:focus,
.select-input:focus,
.form-input:focus {
  outline: none;
  border-color: #40a9ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.date-picker {
  width: 200px;
}

/* 消息提示样式 */
.message-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  z-index: 2000;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.message-toast.success {
  background-color: #52c41a;
}

.message-toast.error {
  background-color: #ff4d4f;
}

.message-toast.warning {
  background-color: #faad14;
}

.message-toast.info {
  background-color: #1890ff;
}

/* 图标样式 */
.icon {
  margin-right: 4px;
}

@media (max-width: 768px) {
  .form-container {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-container > * {
    margin-bottom: 10px;
  }
}
</style>
